/**
 * A client's onboarding checklist item as `client-onboarding-tasks` returns it
 * (API: services/organization/client-onboarding-tasks/). A hand-kept mirror.
 *
 * The client's STAGE is `clients.onboardingStep` (the Overview's stepper); an
 * item files under one of the same stages (`stepKey`). The go-live DATE is not
 * here: it is the client's Whiteboard lane target, shown beside the checklist.
 *
 * Pure (no React, no MUI).
 */
import { ONBOARDING_STAGES, OPEN_TASK_STATUSES } from "shared/trackers/vocabulary";

export interface OnboardingTaskRecord {
  id: string;
  clientId: string;
  stepKey?: string | null;
  title: string;
  ownerUserId?: number | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  dueDate?: string | null;
  status: string;
  completedOn?: string | null;
  completedBy?: number | null;
  notes?: string | null;
  sortOrder?: number;
  sourceSystem?: string | null;
}

export const isOpenTask = (t: Pick<OnboardingTaskRecord, "status">): boolean => OPEN_TASK_STATUSES.has(t.status);

/** Open and past its due day (today is not late). */
export const isOverdueTask = (t: Pick<OnboardingTaskRecord, "status" | "dueDate">, today: string): boolean =>
  isOpenTask(t) && !!t.dueDate && t.dueDate < today;

export interface ChecklistProgress {
  total: number;
  done: number;
  skipped: number;
  open: number;
  overdue: number;
  /** The open item due soonest (undated after dated), or null. */
  next: OnboardingTaskRecord | null;
}

/** How a checklist stands. */
export function checklistProgress(tasks: readonly OnboardingTaskRecord[], today: string): ChecklistProgress {
  const open = tasks.filter(isOpenTask);
  const next =
    [...open].sort(
      (a, b) => (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") || (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    )[0] ?? null;
  return {
    total: tasks.length,
    done: tasks.filter((t) => t.status === "done").length,
    skipped: tasks.filter((t) => t.status === "skipped").length,
    open: open.length,
    overdue: open.filter((t) => isOverdueTask(t, today)).length,
    next,
  };
}

/** Checklist order: by stage (the stepper's order, unfiled last), then the item's own order. */
export function sortTasks(tasks: readonly OnboardingTaskRecord[]): OnboardingTaskRecord[] {
  const stageIndex = (key: string | null | undefined) => {
    const i = ONBOARDING_STAGES.findIndex((s) => s.key === key);
    return i < 0 ? ONBOARDING_STAGES.length : i;
  };
  return [...tasks].sort(
    (a, b) =>
      stageIndex(a.stepKey) - stageIndex(b.stepKey) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.title.localeCompare(b.title),
  );
}

/** The standard checklist: one item per stage the client has no item for yet. */
export function missingStandardItems(
  clientId: string,
  tasks: readonly OnboardingTaskRecord[],
): Array<{ clientId: string; title: string; stepKey: string; sortOrder: number }> {
  const filed = new Set(tasks.map((t) => t.stepKey).filter(Boolean));
  return ONBOARDING_STAGES.filter((s) => !filed.has(s.key)).map((s) => ({
    clientId,
    title: s.label,
    stepKey: s.key,
    sortOrder: ONBOARDING_STAGES.findIndex((x) => x.key === s.key) * 10,
  }));
}

export interface TaskForm {
  clientId: string;
  title: string;
  stepKey: string;
  ownerUserId: string;
  dueDate: string;
  status: string;
  completedOn: string;
  notes: string;
}

export const emptyTaskForm = (clientId = ""): TaskForm => ({
  clientId,
  title: "",
  stepKey: "",
  ownerUserId: "",
  dueDate: "",
  status: "todo",
  completedOn: "",
  notes: "",
});

export const formFromTask = (t: OnboardingTaskRecord): TaskForm => ({
  clientId: t.clientId,
  title: t.title,
  stepKey: t.stepKey ?? "",
  ownerUserId: t.ownerUserId === null || t.ownerUserId === undefined ? "" : String(t.ownerUserId),
  dueDate: t.dueDate ?? "",
  status: t.status,
  completedOn: t.completedOn ?? "",
  notes: t.notes ?? "",
});

/**
 * The API body for a form. `completedOn` is sent only for a done item with a
 * day typed; otherwise the API stamps today (and clears it when not done).
 */
export function taskBody(form: TaskForm): Record<string, unknown> {
  const body: Record<string, unknown> = {
    title: form.title.trim(),
    stepKey: form.stepKey || null,
    ownerUserId: form.ownerUserId ? Number(form.ownerUserId) : null,
    dueDate: form.dueDate || null,
    status: form.status || "todo",
    notes: form.notes.trim() || null,
  };
  if (form.status === "done" && form.completedOn) body.completedOn = form.completedOn;
  return body;
}

export function taskFormProblem(form: TaskForm): string | null {
  if (!form.clientId) return "Pick the client.";
  if (!form.title.trim()) return "Say what the item is.";
  return null;
}
